import React, {
  useMemo, useState,
} from 'react';

import {
  Alert,
  Box,
  Button,
  Grid, Skeleton,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';

import CommitForm from '../../../../components/CommitForm';
import ContentCard from '../../../../components/ContentCard';
import { DateTimePicker } from '../../../../components/DatePicker';
import DayTimePicker from '../../../../components/DayTimePicker';
import Modal from '../../../../components/Modal';
import NotesField from '../../../../components/NotesField';
import ProfileIcon from '../../../../components/ProfileIcon';
import { Stack } from '../../../../components/SkeletonStack/index.styles';
import Tabs from '../../../../components/Tabs';
import { useUpdateClassMutation } from '../../../../services/classes';
import { Class, Lesson } from '../../../../services/classes/types';
import { useGetStaffQuery } from '../../../../services/staff';
import { Metadata } from '../../../../services/types';
import useError from '../../../../utils/react/hooks/useError';
import useSuccess from '../../../../utils/react/hooks/useSuccess';
import ClassLessonCard from '../../ClassLessonCard';

enum Tab {
  ChangeLessons = 'changeLessons',
  KeepLessons = 'keepLessons',
}

interface ClassCardProps {
  classObj: Class;
  classObjMetadata?: Metadata;
  lessons?: Lesson[];
}

const ClassCard = ({ classObj, classObjMetadata = {}, lessons }: ClassCardProps) => {
  const { data: teacher, isLoading: isTeacherLoading } = useGetStaffQuery({
    staffId: classObj.teacher ?? '',
  });
  const tabOptions = [
    {
      label: 'Change Scheduled Lessons',
      key: Tab.ChangeLessons,
    },
    {
      label: 'Change Time Only',
      key: Tab.KeepLessons,
    },
  ];

  const [updateClass, { isLoading: isUpdating,
    isError: isUpdateClassError,
    error: updateClassError,
    isSuccess: isUpdateClassSuccess }] = useUpdateClassMutation();

  const [isChangeClassDatetimeOpen, setChangeClassDatetimeOpen] = useState(false);
  // Default to the class day & time
  const [changeClassDatetime, setChangeClassDatetime] = useState({
    day: classObj.day, time: dayjs(classObj.time, 'HHmm'),
  });
  const [changeClassDatetimeFrom, setChangeClassDatetimeFrom] = useState(dayjs());
  const [selectedTab, setSelectedTab] = useState<Tab>(Tab.ChangeLessons);

  const [newNotes, setNewNotes] = useState(classObj.notes ?? '');

  const firstChangedTimeLesson = useMemo(() => {
    const comparisonValue = changeClassDatetimeFrom.valueOf();
    return lessons?.find((l) => l.startDatetime > comparisonValue);
  }, [changeClassDatetimeFrom, lessons]);

  useError(isUpdateClassError, updateClassError);
  useSuccess(isUpdateClassSuccess, 'Class updated');

  const onClassDatetimeModalClose = () => {
    setChangeClassDatetimeOpen(false);
  };

  if (isTeacherLoading) {
    return (
      <Skeleton height={180} variant="rectangular" />
    );
  }

  return (
    <ContentCard
      title="Class Details"
      buttons={[
        {
          label: 'Schedule Time Change',
          onClick: () => setChangeClassDatetimeOpen(true),
          disabled: !classObj.active,
        },
      ]}
    >
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          md={3}
          alignItems="center"
          justifyContent="center"
          display="flex"
          flexDirection="column"
        >
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            display="flex"
            flexDirection="column"
            spacing={1}
          >
            <ProfileIcon
              firstName={teacher?.data.firstName ?? 'Unassigned'}
              lastName={teacher?.data.lastName}
              imageUrl={teacher?.data.profileIcon?.image}
              color={teacher?.data.profileIcon?.color}
              size={96}
            />
            {!isUpdating && classObj.teacher && (
              <Button
                onClick={() => updateClass({
                  subject: classObj.subject,
                  classId: classObj.classId,
                  newAttributes: {
                    teacher: '',
                  },
                })}
                color="darkPrimary"
                size="small"
              >
                Unassign
              </Button>
            )}
          </Stack>
        </Grid>
        <Grid item xs={12} md={9}>
          <CommitForm
            data={classObj}
            metadata={{
              ...classObjMetadata,
              day: {
                ...classObjMetadata.day,
                editable: false,
              },
              time: {
                ...classObjMetadata.time,
                editable: false,
              },
            }}
            fields={{
              teacher: {
                xs: 12,
                md: 12,
              },
              day: {
                xs: 12,
                md: 12,
              },
              time: {
                xs: 12,
                md: 12,
              },
            }}
            onSubmit={(changes) => updateClass({
              subject: classObj.subject,
              classId: classObj.classId,
              newAttributes: changes,
            })}
            isSubmitLoading={isUpdating}
            autoSubmit
          />
          <Modal
            open={isChangeClassDatetimeOpen}
            title="Schedule Time Change"
            handleClose={onClassDatetimeModalClose}
            buttons={[
              {
                label: 'Change',
                onClick: () => {
                  const newAttributes = selectedTab === Tab.ChangeLessons ? {
                    day: changeClassDatetime.day,
                    time: changeClassDatetime.time.format('HHmm'),
                    changeRegularLessons: true,
                    changeFromDatetime: changeClassDatetimeFrom.unix() * 1000,
                  } : {
                    day: changeClassDatetime.day,
                    time: changeClassDatetime.time.format('HHmm'),
                    changeRegularLessons: false,
                  };

                  updateClass({
                    subject: classObj.subject,
                    classId: classObj.classId,
                    newAttributes,
                  });
                  onClassDatetimeModalClose();
                },
                color: 'darkPrimary',
              },
            ]}
          >
            <Stack direction="column" spacing={2} marginTop={2}>
              <Tabs
                selectedKey={selectedTab}
                onChange={(tab) => setSelectedTab(tab as Tab)}
                options={tabOptions}
                dense
              />
              {selectedTab === Tab.ChangeLessons && (
                <>
                  <Typography>
                    New class time:
                  </Typography>
                  <DayTimePicker
                    value={changeClassDatetime}
                    onChange={(newDate) => setChangeClassDatetime(newDate)}
                  />
                  <Typography>
                    Change regular class times from:
                  </Typography>
                  <DateTimePicker
                    value={changeClassDatetimeFrom}
                    onChange={(newDate) => {
                      if (newDate) setChangeClassDatetimeFrom(newDate);
                    }}
                  />
                  <Alert severity="warning">
                    Lessons scheduled <b>at the original class time</b> will be updated
                    from the following lesson onwards.
                  </Alert>
                  {firstChangedTimeLesson ? (
                    <ClassLessonCard
                      lesson={firstChangedTimeLesson}
                    />
                  )
                    : (
                      <ContentCard>
                        <Box padding={2}>
                          <Typography variant="overline">
                            No lessons after this date
                          </Typography>
                        </Box>
                      </ContentCard>
                    )}
                </>
              )}
              {selectedTab === 'keepLessons' && (
                <>
                  <Alert severity="warning">
                    Existing lessons will remain unaffected.
                  </Alert>
                  <Typography>
                    New class time:
                  </Typography>
                  <DayTimePicker
                    value={changeClassDatetime}
                    onChange={(newDate) => setChangeClassDatetime(newDate)}
                  />
                </>
              )}
            </Stack>
          </Modal>
        </Grid>
        <Grid item xs={12}>
          <NotesField
            placeholder="Class notes..."
            onChange={(event) => setNewNotes(event.target.value)}
            value={newNotes}
            onBlur={() => updateClass({
              subject: classObj.subject,
              classId: classObj.classId,
              newAttributes: {
                notes: newNotes,
              },
            })}
          />
        </Grid>
      </Grid>
    </ContentCard>
  );
};

export default ClassCard;
